import { create } from 'zustand'

/// Light / dark / system, like the iOS ThemeStore (cycle order light → dark →
/// system). The choice is a per-browser convenience in localStorage; the
/// resolved theme is `data-theme` on <html>, which the tokens in styles.css
/// key off. index.html applies it before first paint.

export type ThemeChoice = 'light' | 'dark' | 'system'

const KEY = 'club.theme'

const read = (): ThemeChoice => {
  try {
    const raw = localStorage.getItem(KEY)
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'
  } catch {
    return 'system'
  }
}

const systemDark = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches

export const resolveTheme = (choice: ThemeChoice, prefersDark: boolean): 'light' | 'dark' =>
  choice === 'system' ? (prefersDark ? 'dark' : 'light') : choice

const apply = (choice: ThemeChoice): void => {
  document.documentElement.dataset['theme'] = resolveTheme(choice, systemDark())
}

type ThemeState = Readonly<{
  choice: ThemeChoice
  set: (choice: ThemeChoice) => void
}>

export const useTheme = create<ThemeState>((set) => ({
  choice: read(),
  set: (choice) => {
    try {
      localStorage.setItem(KEY, choice)
    } catch {
      // the choice lasts this tab only
    }
    apply(choice)
    set({ choice })
  },
}))

/// Call once at start-up: applies the stored choice and follows the OS while
/// the choice is «system».
export const startTheme = (): void => {
  apply(useTheme.getState().choice)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useTheme.getState().choice === 'system') apply('system')
  })
}

export const nextTheme = (choice: ThemeChoice): ThemeChoice =>
  choice === 'light' ? 'dark' : choice === 'dark' ? 'system' : 'light'
